import { useCallback, useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  BROKER_ORIGIN,
  DEFAULT_MAC_HOST_ID,
  hostIdError,
  installCommand,
  joinRequestKind,
  kindLabel,
  netSummary,
  normalizeHostId,
  peerSyncPrompt,
  ttlCountdown,
  type BoxKind,
  type JoinNet,
} from "@/lib/macosHosts";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/toast";
import { api, ApiError } from "@/lib/api";
import { useAuthToken } from "@/lib/auth";
import { onDashboardChanged } from "@/lib/dashboardWs";
import { relativeTime } from "@/lib/utils";

interface BootstrapToken {
  token_hash: string;
  token_prefix: string | null;
  created_by: string;
  created_at: string;
  expires_at: string;
  used_at: string | null;
  revoked_at: string | null;
  /** "macos-tester" for a token minted by "Add macOS host" (bound to bound_host_id). */
  purpose?: string | null;
  bound_host_id?: string | null;
}
interface JoinRequest {
  id: string;
  claimed_hostname: string;
  requested_components: string; // JSON-encoded string[]
  csr_fingerprint: string;
  created_at: string;
  /** A macOS host reports its LAN address in the join request (peer firewall). */
  net?: JoinNet | null;
}
interface Box {
  hostname: string;
  spiffe_id: string;
  first_seen_at: string;
  decided_at: string | null;
  /** Derived by the broker from the SPIFFE id prefix. */
  kind?: BoxKind | null;
}
interface BoxComponent {
  spiffe_id: string;
  component: string;
  // Nullable since migration 0067: a workload-identity-only row (runner,
  // publish — see joinFlow.ts's approveJoinRequest) has no network
  // address, unlike proxy/queue/spire/openbao's real topology
  // registrations this table originally held exclusively.
  host: string | null;
  port: number | null;
  requested_at: string;
  decided_at: string | null;
}

function tokenStatus(
  t: BootstrapToken,
): "pending" | "used" | "revoked" | "expired" {
  if (t.revoked_at) return "revoked";
  if (t.used_at) return "used";
  if (new Date(t.expires_at).getTime() < Date.now()) return "expired";
  return "pending";
}

function errMsg(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

/** Linux deploy-agent vs the macOS host, from the SPIFFE id prefix (the broker derives it). */
function KindBadge({ kind }: { kind: BoxKind | null | undefined }) {
  return (
    <Badge variant={kind === "macos" ? "info" : "secondary"}>
      {kindLabel(kind)}
    </Badge>
  );
}

export function BoxApprovalsPage() {
  const token = useAuthToken();
  const call = useCallback(
    <T,>(path: string, init?: RequestInit) => api<T>(token, path, init),
    [token],
  );

  const [tokens, setTokens] = useState<BootstrapToken[] | null>(null);
  const [pending, setPending] = useState<JoinRequest[] | null>(null);
  const [pendingBoxes, setPendingBoxes] = useState<Box[] | null>(null);
  const [pendingComponents, setPendingComponents] = useState<
    BoxComponent[] | null
  >(null);
  const [approvedBoxes, setApprovedBoxes] = useState<Box[] | null>(null);
  const [approvedComponents, setApprovedComponents] = useState<
    BoxComponent[] | null
  >(null);
  const [genResult, setGenResult] = useState<{
    token: string;
    expires_at: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [macHostId, setMacHostId] = useState(DEFAULT_MAC_HOST_ID);
  const [macHostError, setMacHostError] = useState<string | null>(null);
  const [macResult, setMacResult] = useState<{
    token: string;
    expires_at: string;
    hostId: string;
  } | null>(null);
  // ticks once a second while a macOS install command is on screen (TTL countdown)
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!macResult) return;
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [macResult]);
  const macTtl = macResult ? ttlCountdown(macResult.expires_at, nowMs) : null;

  const loadTokens = useCallback(() => {
    call<{ ok: true; tokens: BootstrapToken[] }>("/bootstrap-tokens")
      .then((r) => setTokens(r.tokens))
      .catch((err) => setError(errMsg(err, "failed to load tokens")));
  }, [call]);
  const loadPending = useCallback(() => {
    call<{ ok: true; requests: JoinRequest[] }>("/join-requests?status=pending")
      .then((r) => setPending(r.requests))
      .catch((err) => setError(errMsg(err, "failed to load pending requests")));
  }, [call]);
  const loadPendingBoxes = useCallback(() => {
    call<{ ok: true; boxes: Box[] }>("/boxes?status=pending")
      .then((r) => setPendingBoxes(r.boxes))
      .catch((err) => setError(errMsg(err, "failed to load pending boxes")));
  }, [call]);
  const loadPendingComponents = useCallback(() => {
    call<{ ok: true; components: BoxComponent[] }>(
      "/box-components?status=pending",
    )
      .then((r) => setPendingComponents(r.components))
      .catch((err) =>
        setError(errMsg(err, "failed to load pending components")),
      );
  }, [call]);
  const loadApprovedBoxes = useCallback(() => {
    call<{ ok: true; boxes: Box[] }>("/boxes?status=approved")
      .then((r) => setApprovedBoxes(r.boxes))
      .catch((err) => setError(errMsg(err, "failed to load approved boxes")));
  }, [call]);
  const loadApprovedComponents = useCallback(() => {
    call<{ ok: true; components: BoxComponent[] }>(
      "/box-components?status=approved",
    )
      .then((r) => setApprovedComponents(r.components))
      .catch((err) =>
        setError(errMsg(err, "failed to load approved components")),
      );
  }, [call]);

  const loadAll = useCallback(() => {
    loadTokens();
    loadPending();
    loadPendingBoxes();
    loadPendingComponents();
    loadApprovedBoxes();
    loadApprovedComponents();
  }, [
    loadTokens,
    loadPending,
    loadPendingBoxes,
    loadPendingComponents,
    loadApprovedBoxes,
    loadApprovedComponents,
  ]);

  useEffect(loadAll, [loadAll]);
  useEffect(() => onDashboardChanged(token, loadAll), [token, loadAll]);

  async function generateToken() {
    try {
      const res = await call<{ ok: true; token: string; expires_at: string }>(
        "/bootstrap-token",
        { method: "POST" },
      );
      setGenResult(res);
      loadTokens();
    } catch (err) {
      toast(errMsg(err, "failed to generate token"));
    }
  }

  // "Add macOS host": a one-time token bound to ONE host id (purpose macos-tester, 15 minutes), shown as
  // the install one-liner. The install script is public and static; the token is the only credential and
  // the broker accepts it exactly once, for that host id.
  async function mintMacToken() {
    const hostId = normalizeHostId(macHostId);
    const problem = hostIdError(hostId);
    setMacHostError(problem);
    if (problem) return;
    try {
      const res = await call<{ ok: true; token: string; expires_at: string }>(
        "/bootstrap-token",
        {
          method: "POST",
          body: JSON.stringify({ purpose: "macos-tester", host_id: hostId }),
        },
      );
      setMacResult({ ...res, hostId });
      setMacHostId(hostId);
      loadTokens();
    } catch (err) {
      toast(errMsg(err, "failed to generate the macOS install command"));
    }
  }

  async function revokeToken(hash: string) {
    try {
      await call(`/bootstrap-tokens/${encodeURIComponent(hash)}/revoke`, {
        method: "POST",
      });
      toast("Revoked.");
      loadTokens();
    } catch (err) {
      toast(errMsg(err, "failed to revoke token"));
    }
  }
  async function archiveToken(hash: string) {
    try {
      await call(`/bootstrap-tokens/${encodeURIComponent(hash)}/archive`, {
        method: "POST",
      });
      loadTokens();
    } catch (err) {
      toast(errMsg(err, "failed to archive token"));
    }
  }
  // Approving a macOS host is gated on the connected Linux hosts acknowledging the
  // untrusted-peer firewall sync (plan 7.8): the broker answers 409 {needs_override} and the
  // operator may approve anyway. Anything else is an ordinary error.
  async function postApproval(path: string): Promise<boolean> {
    try {
      await call(path, { method: "POST", body: JSON.stringify({}) });
      return true;
    } catch (err) {
      const prompt =
        err instanceof ApiError && err.status === 409
          ? peerSyncPrompt(err.body)
          : null;
      if (!prompt) throw err;
      if (!window.confirm(prompt)) {
        toast(
          "Not approved: the Linux hosts have not confirmed the firewall rules.",
        );
        return false;
      }
      await call(path, {
        method: "POST",
        body: JSON.stringify({ override_peer_sync: true }),
      });
      return true;
    }
  }
  async function approveJoinRequest(id: string) {
    try {
      if (await postApproval(`/join-requests/${id}/approve`)) {
        toast("Approved.");
        loadPending();
        loadPendingBoxes();
      }
    } catch (err) {
      toast(errMsg(err, "failed to approve"));
    }
  }
  async function rejectJoinRequest(id: string) {
    try {
      await call(`/join-requests/${id}/reject`, { method: "POST" });
      loadPending();
    } catch (err) {
      toast(errMsg(err, "failed to reject"));
    }
  }
  const kindQuery = (kind: BoxKind | null | undefined) =>
    kind ? `?kind=${kind}` : "";
  async function approveBox(hostname: string, kind?: BoxKind | null) {
    try {
      if (
        await postApproval(
          `/boxes/${encodeURIComponent(hostname)}/approve${kindQuery(kind)}`,
        )
      ) {
        toast("Approved.");
        loadPendingBoxes();
        loadApprovedBoxes();
      }
    } catch (err) {
      toast(errMsg(err, "failed to approve box"));
    }
  }
  async function revokeBox(hostname: string, kind?: BoxKind | null) {
    try {
      await call(
        `/boxes/${encodeURIComponent(hostname)}/revoke${kindQuery(kind)}`,
        { method: "POST" },
      );
      toast("Revoked.");
      loadApprovedBoxes();
    } catch (err) {
      toast(errMsg(err, "failed to revoke box"));
    }
  }
  async function approveComponent(hostname: string, component: string) {
    try {
      await call(
        `/box-components/${encodeURIComponent(hostname)}/${encodeURIComponent(component)}/approve`,
        { method: "POST" },
      );
      toast("Approved.");
      loadPendingComponents();
      loadApprovedComponents();
    } catch (err) {
      toast(errMsg(err, "failed to approve component"));
    }
  }
  async function revokeComponent(hostname: string, component: string) {
    try {
      await call(
        `/box-components/${encodeURIComponent(hostname)}/${encodeURIComponent(component)}/revoke`,
        { method: "POST" },
      );
      toast("Revoked.");
      loadApprovedComponents();
    } catch (err) {
      toast(errMsg(err, "failed to revoke component"));
    }
  }
  function componentHostname(spiffeId: string): string {
    return spiffeId.split("/").pop() ?? spiffeId;
  }
  // A workload-identity-only row (runner, publish) has no network
  // address — migration 0067's host/port are NULL there, unlike a real
  // proxy/queue/spire/openbao topology registration.
  function componentAddress(host: string | null, port: number | null): string {
    return host !== null && port !== null
      ? `${host}:${port}`
      : "— (workload identity only)";
  }
  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast("Copied");
    } catch {
      /* clipboard unavailable — the token box itself still shows the value to select manually */
    }
  }

  return (
    <div className="max-w-5xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Box Approvals</h1>
        <p className="text-sm text-muted-foreground">
          Bring new boxes online and approve their access.
        </p>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <Card>
        <CardHeader>
          <CardTitle>Bring a new box online</CardTitle>
          <CardDescription>
            Generate a one-time token, hand it to the new box out-of-band
            (however you already get root on it), then run{" "}
            <code className="bg-muted px-1 py-0.5 rounded">
              sudo pure-deploy install &lt;component&gt;
            </code>{" "}
            there with the token when prompted.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button onClick={generateToken}>Generate bootstrap token</Button>
          {genResult && (
            <div className="space-y-2">
              <div
                className="bg-muted p-3 rounded-md font-mono text-sm break-all cursor-pointer"
                title="Click to copy"
                onClick={() => copy(genResult.token)}
              >
                {genResult.token}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => copy(genResult.token)}
              >
                Copy
              </Button>
              <p className="text-xs text-muted-foreground">
                Expires {relativeTime(genResult.expires_at)}. Shown once — copy
                it now.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Add macOS host</CardTitle>
          <CardDescription>
            Enroll an Apple-silicon Mac (<code>macos-tester</code>) as a fleet
            host. This mints a one-time token bound to that host id and shows
            the single command to run on the Mac. The Mac gets its own SPIFFE
            identity (<code>spiffe://pure.dev/macos-tester/&lt;host&gt;</code>),
            then appears under Pending approvals below with the LAN address it
            reported; approving it first makes the Linux hosts deny that address
            (except SPIRE), and only then lets the Mac attest.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label htmlFor="mac-host-id">Host id</Label>
              <Input
                id="mac-host-id"
                value={macHostId}
                onChange={(e) => setMacHostId(e.target.value)}
                aria-invalid={macHostError ? true : undefined}
                aria-describedby={
                  macHostError ? "mac-host-id-error" : undefined
                }
                className="w-64 font-mono"
                spellCheck={false}
                autoComplete="off"
              />
            </div>
            <Button onClick={mintMacToken}>Generate install command</Button>
          </div>
          {macHostError && (
            <p
              id="mac-host-id-error"
              role="alert"
              className="text-sm text-destructive"
            >
              {macHostError}
            </p>
          )}
          {macResult && macTtl && (
            <div className="space-y-2">
              <pre
                aria-label="macOS install command"
                className={`bg-muted p-3 rounded-md font-mono text-sm whitespace-pre-wrap break-all ${macTtl.expired ? "opacity-50 line-through" : ""}`}
              >
                {installCommand(
                  BROKER_ORIGIN,
                  macResult.token,
                  macResult.hostId,
                )}
              </pre>
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={macTtl.expired}
                  onClick={() =>
                    copy(
                      installCommand(
                        BROKER_ORIGIN,
                        macResult.token,
                        macResult.hostId,
                      ),
                    )
                  }
                >
                  Copy command
                </Button>
                <span
                  className={`text-sm ${macTtl.expired ? "text-destructive" : "text-muted-foreground"}`}
                  role="timer"
                  aria-live="off"
                >
                  {macTtl.expired
                    ? "Expired. Generate a new command."
                    : `Valid for ${macTtl.label}`}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Single use, for host <code>{macResult.hostId}</code> only. Run
                it in Terminal on the Mac (it needs sudo). Shown once: copy it
                now. Nothing secret is in the script itself; every file it
                fetches is signature and checksum verified.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Bootstrap tokens</CardTitle>
          <CardDescription>
            Past tokens, masked to their first few characters (the full value is
            never stored once shown). Revoke one early if it was generated for
            the wrong box or pasted somewhere it shouldn't have been; archive
            one to hide it here once you're done with it.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Token</TableHead>
                <TableHead>Created by</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Status</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {tokens?.map((t) => {
                const status = tokenStatus(t);
                return (
                  <TableRow key={t.token_hash}>
                    <TableCell className="font-mono text-xs">
                      {(t.token_prefix ?? "????????") + "…"}
                      {t.purpose === "macos-tester" && (
                        <Badge
                          variant="outline"
                          className="ml-2"
                          title="one-time macOS install token, bound to this host id"
                        >
                          macOS · {t.bound_host_id ?? "?"}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>{t.created_by}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {relativeTime(t.created_at)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {relativeTime(t.expires_at)}
                    </TableCell>
                    <TableCell>{status}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {status === "pending" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => revokeToken(t.token_hash)}
                        >
                          Revoke
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => archiveToken(t.token_hash)}
                      >
                        Archive
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {tokens?.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No tokens generated yet.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Pending approvals</CardTitle>
          <CardDescription>
            New boxes requesting a signed identity for the first time.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Hostname</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Components</TableHead>
                <TableHead>Reported address</TableHead>
                <TableHead>Key fingerprint</TableHead>
                <TableHead>Requested</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {pending?.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.claimed_hostname}</TableCell>
                  <TableCell>
                    <KindBadge kind={joinRequestKind(r.requested_components)} />
                  </TableCell>
                  <TableCell>
                    {JSON.parse(r.requested_components).join(", ")}
                  </TableCell>
                  <TableCell
                    className="text-xs"
                    title="Compare once with the router's client list. The Linux hosts deny this address (except SPIRE) before the Mac can attest."
                  >
                    {joinRequestKind(r.requested_components) === "macos"
                      ? netSummary(r.net)
                      : "—"}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {r.csr_fingerprint}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {relativeTime(r.created_at)}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" onClick={() => approveJoinRequest(r.id)}>
                      Approve
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => rejectJoinRequest(r.id)}
                    >
                      Reject
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {pending?.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No pending requests.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Pending box connections</CardTitle>
          <CardDescription>
            Boxes that already have a signed identity, asking to actually talk
            to Cloudflare services (this is the separate, second approval: an
            identity alone isn't enough).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Hostname</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Identity</TableHead>
                <TableHead>First seen</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {pendingBoxes?.map((b) => (
                <TableRow key={b.spiffe_id}>
                  <TableCell>{b.hostname}</TableCell>
                  <TableCell>
                    <KindBadge kind={b.kind} />
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {b.spiffe_id}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {relativeTime(b.first_seen_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      onClick={() => approveBox(b.hostname, b.kind)}
                    >
                      Approve
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {pendingBoxes?.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No pending connections.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Pending component registrations</CardTitle>
          <CardDescription>
            An already-connected box asking to register a specific component
            (proxy, queue, spire, openbao) in the topology registry. Approving
            one component never approves another for the same box.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Hostname</TableHead>
                <TableHead>Component</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Requested</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {pendingComponents?.map((c) => (
                <TableRow key={c.spiffe_id + c.component}>
                  <TableCell>{componentHostname(c.spiffe_id)}</TableCell>
                  <TableCell>{c.component}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {componentAddress(c.host, c.port)}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {relativeTime(c.requested_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      onClick={() =>
                        approveComponent(
                          componentHostname(c.spiffe_id),
                          c.component,
                        )
                      }
                    >
                      Approve
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {pendingComponents?.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No pending component registrations.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Approved box connections</CardTitle>
          <CardDescription>
            Revoking one immediately disconnects that box's deploy-agent and
            blocks it from reconnecting until re-approved.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Hostname</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Identity</TableHead>
                <TableHead>Approved</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {approvedBoxes?.map((b) => (
                <TableRow key={b.spiffe_id}>
                  <TableCell>{b.hostname}</TableCell>
                  <TableCell>
                    <KindBadge kind={b.kind} />
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {b.spiffe_id}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {relativeTime(b.decided_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => revokeBox(b.hostname, b.kind)}
                    >
                      Revoke
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {approvedBoxes?.length === 0 && (
            <p className="text-sm text-muted-foreground">No approved boxes.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Approved component registrations</CardTitle>
          <CardDescription>
            proxy/queue/spire/openbao rows have a real network address —
            revoking one removes that box's entry from the topology registry
            immediately, and other boxes stop discovering it there.
            runner/publish/macos-tester rows ("— (workload identity only)")
            instead reflect the SPIRE workload entry approved alongside that
            box's join request (Box Approvals above) — revoking one here doesn't
            reach into spire-server itself.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Hostname</TableHead>
                <TableHead>Component</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Approved</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {approvedComponents?.map((c) => (
                <TableRow key={c.spiffe_id + c.component}>
                  <TableCell>{componentHostname(c.spiffe_id)}</TableCell>
                  <TableCell>{c.component}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {componentAddress(c.host, c.port)}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {relativeTime(c.decided_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() =>
                        revokeComponent(
                          componentHostname(c.spiffe_id),
                          c.component,
                        )
                      }
                    >
                      Revoke
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {approvedComponents?.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No approved component registrations.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
